import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { cn } from '@vd/ui';
import { MentionTextarea, type MentionTextareaProps } from '../mentions/MentionTextarea';
import { MarkdownBody } from '../comments/MarkdownBody';
import styles from './DescriptionField.module.css';

/**
 * A ticket's description, shown formatted until somebody edits it.
 *
 * Descriptions are markdown by habit: the walkthrough recorder's rewrite
 * writes bold and numbered lists, and so do agents through MCP. The field
 * showed them raw, asterisks and all (Jason, 2 Oct 2026). While the textarea
 * does not have focus and holds something, the same text is laid over it
 * formatted, through the comments' renderer, so it is safe the same way.
 *
 * The textarea stays mounted underneath, so everything that works through it
 * keeps working: the ref dictation types into, autosave on blur, the mention
 * picker. Focusing it, by a click on the formatted text, by Tab, or by
 * dictation starting, shows the text as written for editing; leaving it shows
 * the formatted text again.
 *
 * The formatted copy is hidden from assistive technology, which reads the
 * textarea, and its links are left out of the Tab order for the same reason:
 * the keyboard path is the textarea. A click on a link opens it rather than
 * starting an edit, and does not leave the focus on the link either.
 *
 * Choices made on purpose: a click anywhere else puts the caret at the end of
 * the text, since a place in the formatted copy has no exact place in the
 * text as written; the formatted text cannot be selected, since a press on it
 * starts an edit; and the textarea's resize handle works while editing. The
 * copy wears the caller's class, so that class must give an opaque box, as
 * the textarea's does.
 */
export const DescriptionField = forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  function DescriptionField({ className, onFocus, onBlur, ...props }, ref) {
    const textarea = useRef<HTMLTextAreaElement | null>(null);
    // Read on every render rather than once, so a textarea replaced underneath
    // is the one dictation types into.
    useImperativeHandle(ref, () => textarea.current as HTMLTextAreaElement);
    const [editing, setEditing] = useState(false);
    const formatted = !editing && props.value.trim().length > 0;
    return (
      <div className={styles.field}>
        <MentionTextarea
          {...props}
          ref={textarea}
          className={className}
          onFocus={(event) => { setEditing(true); onFocus?.(event); }}
          onBlur={(event) => { setEditing(false); onBlur?.(event); }}
        />
        {formatted && (
          <div
            className={cn(className, styles.formatted)}
            data-testid="description-formatted"
            aria-hidden="true"
            onMouseDown={(event) => {
              // Kept from taking the focus or starting a selection of its
              // own: the textarea is what gets both. A link still opens on the
              // click that follows, without the focus landing on it.
              event.preventDefault();
              if ((event.target as Element).closest('a')) return;
              const field = textarea.current;
              if (!field) return;
              field.focus();
              field.setSelectionRange(field.value.length, field.value.length);
            }}
          >
            <MarkdownBody body={props.value} members={props.members} linksFocusable={false} />
          </div>
        )}
      </div>
    );
  },
);
